import { FiShield, FiZap, FiGlobe, FiCpu, FiSettings } from "react-icons/fi";
import { SiSymfony, SiComposer, SiPhp } from "react-icons/si";
import { LuSettings, LuUsers, LuShieldCheck, LuPercent, LuShield, LuCpu, LuCircleCheck, LuClipboardList, LuCode, LuBug, LuRocket, LuDatabase, LuClock } from "react-icons/lu";

export const symfonyBenefits = [
  {
    icon: <FiSettings className="w-8 h-8" />,
    title: "High Modularities",
    desc: "Symfony's component-based structure allows for absolute flexibility and reuse of features.",
  },
  {
    icon: <FiShield className="w-8 h-8" />,
    title: "Standards-First",
    desc: "Following strict PHP standards (PSR) ensures maintainable and professional codebases.",
  },
  {
    icon: <FiZap className="w-8 h-8" />,
    title: "Proven Stability",
    desc: "One of the most mature frameworks, powering some of the world's largest web platforms.",
  },
];

export const symfonyServices = [
  {
    icon: <SiSymfony size={36} />,
    title: "Custom Symfony Core",
    description: "Building backends with the modular Symfony components for maximum scalability.",
    image: "/images/custom-dev.jpg",
    link: "#",
  },
  {
    icon: <SiComposer size={36} />,
    title: "Reusable Components",
    description: "Developing independent, professional PHP components for wider ecosystem usage.",
    image: "/images/backend-dev.jpg",
    link: "#",
  },
  {
    icon: <SiPhp size={36} />,
    title: "Legacy Modernization",
    description: "Upgrading existing PHP applications to stable Symfony architectures.",
    image: "/images/cms-dev.jpg",
    link: "#",
  },
];

export const symfonyFaqs = [
  {
    q: "Why should I choose Symfony development in India for my project?",
    a: "Symfony development in India offers cost-effective solutions with skilled developers who can build robust, scalable, and secure web applications. Symfony’s flexible PHP framework allows for faster development, easy maintenance, and enterprise-level application support",
  },
  {
    q: "What are the benefits of Symfony development for my business?",
    a: "Symfony development helps create high-performance, secure, and scalable web applications. It provides reusable components, a strong community, and flexibility to customize applications according to your business needs, ensuring long-term reliability and efficiency.",
  },
];

export const symfonyProcessSteps = [
  {
    step: "01",
    title: "Component Strategy",
    description: "Selecting the precise Symfony components needed for your specific feature set.",
    points: ["Dependency mapping", "Configuration strategy", "Bundle design"],
  },
  {
    step: "02",
    title: "Clean Development",
    description: "Building the application using strict OOP patterns and Twig templating if needed.",
    points: ["Controller design", "Service orchestration", "Twig implementation"],
  },
  {
    step: "03",
    title: "Profiling & Checkups",
    description: "Using the Symfony Profiler to ensure zero performance bottlenecks and optimal queries.",
    points: ["Performance profiling", "Unit testing", "Standards check"],
  },
];

export const symfonyTechStack = ["Symfony", "PHP", "Twig", "Doctrine", "Composer", "API Platform", "Messenger"];
export const symfonyHeroData = {
  backgroundImage: "/images/serviceBanners/syfonyBanner.webp",
  whiteImage: "/images/serviceBanners/symphonyW.webp",
  subTitle: "Symfony Development Services",
  title: (
    <>
      Enterprise PHP <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Symfony Solutions</span>
    </>
  ),
  description:
    "We build scalable and modular enterprise PHP applications using Symfony framework with strict PSR standards to ensure reliable, secure, and high performance backend systems for modern digital business needs.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("Get Started clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("View Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Modular Core",
      desc: "Fully decoupled and reusable Symfony components for scalable applications.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "PSR Standards",
      desc: "Strict adherence to PHP standards for clean and maintainable code.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Built-in Security",
      desc: "Advanced security features including CSRF protection, authentication, and validation.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Doctrine ORM",
      desc: "Powerful object-relational mapping for efficient database management.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp", // Re-use generic icon or replace with symfony path asset
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};
export const symfonyStatsData = [
  {
    icon: LuSettings,
    value: "120+",
    label: "Symfony Projects",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "50+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.98%",
    label: "Service Uptime",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuCpu,
    value: "6M+",
    label: "Daily Hits",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "30%",
    label: "Code Reusable",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Audited APIs",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

export const symfonySolutionsAndProcessData = {
  buildSubtitle: "WHAT WE BUILD",
  buildTitle: "Symfony Enterprise Solutions We Build",
  buildCards: [
    {
      title: "Bespoke SaaS Platforms",
      desc: "Build secure multi-tenant SaaS applications using Symfony bundles for scalable enterprise systems.",
      icon: LuSettings,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Decoupled Microservices",
      desc: "Create independent backend services communicating via event-driven architecture and JSON APIs.",
      icon: LuCpu,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "REST & GraphQL APIs",
      desc: "Develop structured and self-documented APIs using Symfony ApiPlatform for flexible data access.",
      icon: LuCode,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Legacy PHP Modernization",
      desc: "Transform legacy PHP codebases into clean, maintainable Symfony MVC architectures.",
      icon: LuClock,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Doctrine DBAL Architecture",
      desc: "Optimize database layers with advanced ORM, migrations, and custom query builders.",
      icon: LuDatabase,
      colorClass: "text-[#6366f1]",
      bgClass: "bg-[#6366f1]/10",
      borderClass: "border-[#6366f1]/20 hover:border-[#6366f1]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(99,102,241,0.25)]",
    },
    {
      title: "Enterprise Web Systems",
      desc: "Build high-traffic applications using caching strategies like HttpCache and Varnish integration.",
      icon: LuRocket,
      colorClass: "text-[#d946ef]",
      bgClass: "bg-[#d946ef]/10",
      borderClass: "border-[#d946ef]/20 hover:border-[#d946ef]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(217,70,239,0.25)]",
    },
  ],
  processSubtitle: "SYMFONY ENGINEERING PROCESS",
  processTitle: "Symfony Engineering Process",
  processSteps: [
    {
      step: "01",
      title: "Dependency Design",
      description: "Define Symfony bundles, packages, and database structure for scalable architecture.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "Routing & Security",
      description: "Configure routes, firewalls, and role-based access control for secure applications.",
      icon: LuCode,
    },
    {
      step: "03",
      title: "Business Logic Development",
      description: "Implement controllers, services, and Doctrine entities for core functionality.",
      icon: LuSettings,
    },
    {
      step: "04",
      title: "Forms & Validation",
      description: "Handle secure form processing with proper input validation and sanitization.",
      icon: LuShieldCheck,
    },
    {
      step: "05",
      title: "Performance Profiling",
      description: "Analyze queries, logs, and execution time for granular system optimization.",
      icon: LuCircleCheck, // Avoid build compilation failures by explicitly tracking with CircleCheck
    },
    {
      step: "06",
      title: "CI/CD Deployment",
      description: "Automate builds, cache management, and production deployment workflows.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE SYMFONY?",
  benefitsTitle: "Why Symfony is the Right Choice",
  benefits: [
    "PSR standards compliance",
    "Modular bundle architecture",
    "Symfony profiler debugging",
    "Secure authentication system",
    "Long-term LTS support",
    "Doctrine Twig integration",
    "Enterprise-grade stability",
  ],
  logoImage: "/images/serviceBanners/syfonyBanner.webp",
  whiteLogoImage: "/images/serviceBanners/symphonyW.webp",
};

export const symfonyPortfolioData = {
  subtitle: "OUR WORK",
  title: "Symfony Solutions We're Proud Of",
  buttonText: "View All Projects",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "SaaS Enterprise Core",
      badge: "Symfony Bundle Core",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Modular SaaS subscription platform built with decoupled PHP components.",
      downloads: "100k+ Users",
      rating: "4.9",
      image: "/images/WebAppDevelopment/Symfony_development.png",
    },
    {
      title: "B2B Order Gateway",
      badge: "Doctrine DBAL & API",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "High-volume transactional order processing backend supporting robust XML/JSON parsing.",
      downloads: "4M+ Requests",
      rating: "5.0",
      image: "/images/WebAppDevelopment/Symfony_development.png",
    },
  ],
};

export const symfonyTechnologiesData = {
  techTitle: "TECHNOLOGIES WE USE",
  technologies: [
    { name: "REST API", icon: "RestAPI" },
    { name: "SQLite", icon: "SQLite" },
    { name: "Firebase", icon: "Firebase" },
    { name: "Provider", icon: "Provider" },
  ],
  trustTitle: "TRUSTED BY BUSINESSES WORLDWIDE",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

export const symfonyAboutData = [
  {
    title: "Symfony Development Services for Scalable Enterprise Applications",
    content: (
      <>
        Modern businesses require backend systems that remain flexible, secure, and easy to maintain as operational needs evolve. Reliable{" "}
        <b className="text-text-primary font-semibold">symfony development services</b> help organizations build structured enterprise applications designed for stability and
        long-term performance. Many businesses struggle with outdated backend systems that limit scalability and slow development efficiency. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, Symfony solutions are developed around business objectives, application flexibility, and maintainable
        architecture to help companies build reliable digital systems for future growth.
      </>
    ),
  },
  {
    title: "Symfony Development Company for Reliable Backend Systems",
    content: (
      <>
        A strong backend foundation is essential for maintaining smooth operations and supporting growing digital ecosystems. A trusted{" "}
        <b className="text-text-primary font-semibold">symfony development company</b> helps businesses create organized server-side architectures that improve workflow management
        and operational consistency. Companies investing in <b className="text-text-primary font-semibold">php symfony development</b> often benefit from cleaner code structures
        and better maintainability because applications are designed for long-term efficiency. Tycho Technologies focuses on practical Symfony frameworks that improve backend
        performance while supporting scalable enterprise operations.
      </>
    ),
  },
  {
    title: "Hire Symfony Developers for Smarter Digital Infrastructure",
    content: (
      <>
        Businesses handling growing digital operations need backend systems that remain stable under changing demands. Companies looking to{" "}
        <b className="text-text-primary font-semibold">hire symfony developers</b> often prioritize performance, maintainability, and stronger application flexibility for long-term
        scalability. Organizations aiming to strengthen backend ecosystems frequently combine Symfony architectures with{" "}
        <a href="https://tychotechnologies.com/laravel-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          laravel development services
        </a>{" "}
        to support adaptable business environments and improve development flexibility.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a Symfony Development Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">symfony development company in india</b> continues to grow as businesses seek scalable and
        future-ready backend technologies. Companies investing in <b className="text-text-primary font-semibold">symfony development services india</b> often focus on operational
        efficiency, structured architecture, and stronger application stability. Businesses partnering with Tycho Technologies gain access to customized Symfony solutions designed
        around measurable business goals and technical requirements. Many organizations also improve backend adaptability through{" "}
        <a href="https://tychotechnologies.com/dotnet-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          dot net web development
        </a>{" "}
        to support modern enterprise systems and connected digital infrastructures.
      </>
    ),
  },
];
